import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { BookOpen, GraduationCap, Landmark, Pencil, Plus, Search, Trash2, Users } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { Field } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuthStore } from "@/store/authStore";
import { useAdminCourses, useDeleteCourse } from "@/hooks/useAdminData";
import { TableSkeleton } from "@/components/shared/Skeletons";

export const Route = createFileRoute("/admin/courses/")({
  component: AdminCoursesPage,
});

const mockCourses = [
  {
    id: "1",
    code: "CS101",
    name: "Computer Science Fundamentals",
    university: "UDSM",
    students: 245,
    status: "active",
  },
  {
    id: "2",
    code: "BA201",
    name: "Business Administration",
    university: "IFM",
    students: 189,
    status: "active",
  },
  {
    id: "3",
    code: "EN301",
    name: "Electrical Engineering",
    university: "DIT",
    students: 156,
    status: "active",
  },
  {
    id: "4",
    code: "ME401",
    name: "Mechanical Engineering",
    university: "UDSM",
    students: 134,
    status: "inactive",
  },
];

function AdminCoursesPage() {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const { user } = useAuthStore();
  const { data: coursesData = [], isLoading } = useAdminCourses();
  const deleteCourseMutation = useDeleteCourse();

  const handleDelete = (id: string | number) => {
    if (window.confirm("Are you sure you want to delete this program?")) {
      deleteCourseMutation.mutate(id);
    }
  };

  const displayCourses = user?.isDemo ? mockCourses : coursesData;

  const filteredCourses = displayCourses.filter((course: any) => {
    const name = course.name || course.title || "";
    const code = course.code || "";
    return (
      name.toLowerCase().includes(search.toLowerCase()) ||
      code.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Academic catalog"
        subtitle="Manage university degree programs, institutional affiliations, and student enrollment trends."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Courses" }]}
        action={
          <>
            <Button variant="outline" asChild>
              <Link to="/admin/courses/discovery">
                <Search />
                Discover & import
              </Link>
            </Button>
            <Button onClick={() => setOpen(true)}>
              <Plus />
              New program
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Total programs" value={displayCourses.length} icon={<BookOpen />} />
        <StatCard
          label="Active cohorts"
          value={displayCourses.filter((c: any) => (c.status || "active") === "active").length}
          icon={<GraduationCap />}
        />
        <StatCard
          label="Total students enrolled"
          value={displayCourses.reduce((sum: number, c: any) => sum + (c.students || 0), 0)}
          icon={<Users />}
        />
      </div>

      <SectionCard
        title="Program directory"
        noPadding
        actions={
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by code or title…"
            aria-label="Search programs"
          />
        }
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={4} />
          </div>
        ) : filteredCourses.length === 0 ? (
          <EmptyState
            bare
            icon={<BookOpen />}
            title="No academic programs found."
            description="Try a different search, or add a new program to the catalog."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Academic program</TableHead>
                <TableHead>Institutional body</TableHead>
                <TableHead>Active students</TableHead>
                <TableHead>Program status</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCourses.map((course: any) => (
                <TableRow key={course.id}>
                  <TableCell className="pl-4 md:pl-5">
                    <div className="flex items-center gap-3">
                      <span
                        className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-background font-mono text-caption text-body"
                        aria-hidden
                      >
                        {(course.code || course.name || "?").charAt(0)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">
                          {course.name || course.title}
                        </p>
                        <p className="font-mono text-caption text-muted-foreground">
                          {course.code || "N/A"}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-2 whitespace-nowrap text-body">
                      <Landmark className="size-3.5 text-muted-foreground" aria-hidden />
                      {course.university || course.institution || "Unknown"}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums text-body">
                    {course.students || 0} students
                  </TableCell>
                  <TableCell>
                    <StatusChip status={course.status || "active"} />
                  </TableCell>
                  <TableCell className="pr-4 text-right md:pr-5">
                    <div className="flex justify-end gap-1">
                      <IconAction label="Edit program" icon={<Pencil />} />
                      <IconAction
                        label="Delete catalog entry"
                        tone="destructive"
                        icon={<Trash2 />}
                        onClick={() => handleDelete(course.id)}
                        disabled={deleteCourseMutation.isPending}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Catalogue expansion initiative</DialogTitle>
            <DialogDescription>
              Configure a new academic program to be recognized within the placement network.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Program title" className="sm:col-span-2">
              <Input placeholder="e.g. BSc in Renewable Energy" />
            </Field>
            <Field label="Program code">
              <Input placeholder="e.g. RE401" />
            </Field>
            <Field label="Primary institution">
              {({ id }) => (
                <Select>
                  <SelectTrigger id={id}>
                    <SelectValue placeholder="Select institution" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="udsm">UDSM</SelectItem>
                    <SelectItem value="ardhi">Ardhi University</SelectItem>
                    <SelectItem value="ifm">IFM</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Program coordinator" className="sm:col-span-2">
              <Input placeholder="Head of Department Name" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setOpen(false)}>Establish program</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
